
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import '../../utils/browserPolyfills'; // Import polyfills first

const SUPABASE_URL = "https://ginefbvgxcobyuocxerb.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdpbmVmYnZneGNvYnl1b2N4ZXJiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzk4Njg0NDksImV4cCI6MjA1NTQ0NDQ0OX0.xm9j209Swb0SWPgbuHsmC0ey6oxP5sxL_dB5PR6Urh0";

// Get the current site URL (works in both development and production)
// This will detect if we're in a browser environment and use the current URL
// or fall back to the appropriate URL for the current environment
const getSiteUrl = () => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}`;
  }
  
  // For SSR and non-browser environments, check for production environment
  // Look for the Lovable.app deployed URL first
  if (process.env.NODE_ENV === 'production' || 
      (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production')) {
    return 'https://lamulamu-12.lovable.app';
  }
  
  // For the deployed app on Lovable's platform, this will catch most cases
  // even if process.env.NODE_ENV isn't set correctly
  // Create a properly typed window reference
  const windowObj = typeof window !== 'undefined' ? window : null;
  const hasLocation = windowObj && 'location' in windowObj;
  const hasHostname = hasLocation && windowObj.location && 'hostname' in windowObj.location;
  const isLovableHosted = hasHostname && 
                         typeof windowObj.location.hostname === 'string' && 
                         windowObj.location.hostname.includes('lovable.app');
  
  if (isLovableHosted) {
    return 'https://lamulamu-12.lovable.app';
  }
  
  // Fall back to a reasonable default that works in development
  return 'http://localhost:8080';
};

// Ensure the redirect URL always points to the /auth route
const getRedirectUrl = () => {
  const baseUrl = getSiteUrl();
  
  // Make sure we don't end up with double slashes in the URL
  const normalizedUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // Always redirect to the /auth path
  return `${normalizedUrl}/auth`;
};

// Create a more robust storage implementation that won't conflict with editor mode detection
// This wraps localStorage but adds additional safeguards
const createStableStorage = () => {
  if (typeof window === 'undefined') return undefined;
  
  // Check if localStorage is available
  let isLocalStorageAvailable = false;
  try {
    localStorage.setItem('supabase_storage_test', 'test');
    localStorage.removeItem('supabase_storage_test');
    isLocalStorageAvailable = true;
  } catch (e) {
    console.warn('[SUPABASE] localStorage not available, falling back to memory storage');
    isLocalStorageAvailable = false;
  }
  
  // If localStorage is not available, return undefined so Supabase uses its memory fallback
  if (!isLocalStorageAvailable) {
    return undefined;
  }
  
  // Create a storage wrapper with extra safeguards
  return {
    getItem: (key: string): string | null => {
      try {
        // Add retry logic for flaky storage in dev mode
        const maxRetries = 2;
        let value = null;
        let attempts = 0;
        
        while (attempts < maxRetries && value === null) {
          value = localStorage.getItem(key);
          attempts++;
          
          if (value === null && attempts < maxRetries) {
            // Small delay between retries
            console.debug(`[SUPABASE] Storage retry ${attempts} for key ${key}`);
          }
        }
        
        return value;
      } catch (error) {
        console.error('[SUPABASE] Error reading from storage:', error);
        return null;
      }
    },
    setItem: (key: string, value: string): void => {
      try {
        localStorage.setItem(key, value);
        
        // For auth-related keys, create a backup in sessionStorage
        // This helps with race conditions in editor mode detection
        if (key.includes('supabase.auth.') || key.includes('sb-')) {
          try {
            sessionStorage.setItem(`backup_${key}`, value);
          } catch (e) {
            // Ignore sessionStorage errors
          }
        }
      } catch (error) {
        console.error('[SUPABASE] Error writing to storage:', error);
      }
    },
    removeItem: (key: string): void => {
      try {
        localStorage.removeItem(key);
        
        // Also remove from backup if it exists
        try {
          sessionStorage.removeItem(`backup_${key}`);
        } catch (e) {
          // Ignore sessionStorage errors
        }
      } catch (error) {
        console.error('[SUPABASE] Error removing from storage:', error);
      }
    }
  };
};

// Get our enhanced storage
const stableStorage = createStableStorage();

// Initialize the Supabase client with proper redirect URLs and enhanced storage
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: stableStorage,
    storageKey: 'lamulamu_supabase_auth',
    flowType: 'pkce' // Using PKCE for more secure auth flow
  },
  global: {
    headers: {
      'apikey': SUPABASE_PUBLISHABLE_KEY
    }
  }
});

// Add error handling and retries for Supabase operations
export const safeSupabaseQuery = async <T>(queryFn: () => Promise<T>): Promise<T> => {
  const MAX_RETRIES = 2;
  let retries = 0;
  let lastError: any = null;
  
  while (retries <= MAX_RETRIES) {
    try {
      return await queryFn();
    } catch (error) {
      lastError = error;
      retries++;
      if (retries <= MAX_RETRIES) {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 300));
      }
    }
  }
  
  console.error('All Supabase query retries failed:', lastError);
  throw lastError;
};

// Function to check if email testing mode is enabled
export async function isTestingModeEnabled() {
  try {
    // Call the ensure_admin_settings function to make sure we have a record
    await supabase.rpc('ensure_admin_settings');
    
    const { data, error } = await supabase
      .from('admin_settings')
      .select('testing_mode, test_email')
      .limit(1)
      .maybeSingle();
    
    // If no record exists or there's an error, return disabled state
    if (error || !data) {
      console.error('Error fetching admin settings:', error);
      return { enabled: false, email: null };
    }
    
    return { 
      enabled: (data as any).testing_mode || false, 
      email: (data as any).test_email || null 
    };
  } catch (error) {
    console.error('Error in isTestingModeEnabled:', error);
    return { enabled: false, email: null };
  }
}

console.log("[SUPABASE] Client initialized with auth storage:", typeof stableStorage !== 'undefined' ? "enhanced localStorage" : "undefined");

// Log session on init
if (typeof window !== 'undefined') {
  supabase.auth.getSession().then(({ data, error }) => {
    if (error) {
      console.error("[SUPABASE] Error getting initial session:", error);
    } else {
      console.log("[SUPABASE] Initial session state:", data.session ? "Active with user " + data.session.user.id : "No session");
    }
  });
}

// Log auth state changes
if (typeof window !== 'undefined') {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    console.log("[SUPABASE] Auth state changed:", event, "session:", session ? "exists with user " + session.user.id : "null");
  });
}

// Helper function to verify the session exists and is valid
export const verifySession = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    return !error && !!data.session;
  } catch (e) {
    console.error("[SUPABASE] Error verifying session:", e);
    return false;
  }
};

// Cleanup function that can be used to ensure a clean auth state
export const cleanupAuthState = () => {
  try {
    if (typeof window === 'undefined') return;
    
    // Get all keys from localStorage
    const allKeys = Object.keys(localStorage);
    
    // Filter auth-related keys
    const authKeys = allKeys.filter(key => 
      key.startsWith('supabase.auth.') || 
      key.includes('sb-') ||
      key.includes('lamulamu_supabase_auth')
    );
    
    // Log what we're removing for debugging
    if (authKeys.length > 0) {
      console.log("[SUPABASE] Cleaning up auth state keys:", authKeys);
    }
    
    // Remove each key
    authKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.error(`[SUPABASE] Error removing key ${key}:`, e);
      }
    });
    
    // Also clean sessionStorage
    try {
      const sessionKeys = Object.keys(sessionStorage).filter(key => 
        key.startsWith('supabase.auth.') || 
        key.includes('sb-') ||
        key.startsWith('backup_')
      );
      
      sessionKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Ignore errors
        }
      });
    } catch (e) {
      // Ignore errors
    }
  } catch (e) {
    console.error("[SUPABASE] Error in cleanupAuthState:", e);
  }
};

// Function to setup testing mode for demo purposes
export async function setupTestEmailInterception() {
  try {
    const { enabled, email } = await isTestingModeEnabled();
    
    if (enabled && email) {
      console.log('Setting up email interception for testing mode with email:', email);
      
      // In testing mode, simulate emails rather than intercepting Supabase's real emails
      // This is because we can't actually intercept the emails Supabase sends
      
      // Configure test mode for the edge function if testing is enabled
      try {
        // Use the dynamically detected URL for redirects
        const redirectUrl = getRedirectUrl();
        console.log('Setting up test email with redirect URL:', redirectUrl);
        
        await supabase.functions.invoke('test-email-handler', {
          method: 'POST',
          body: { 
            email, 
            type: 'config', 
            testing: true,
            redirectTo: redirectUrl
          }
        });
        console.log('Test email configuration sent successfully');
        
        // Listen for auth state changes to simulate events for testing
        supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'USER_UPDATED') {
            console.log('Auth event captured for testing:', event);
          }
        });
      } catch (error) {
        console.error('Failed to set up test email interception:', error);
      }
      
      console.log('Auth configured for testing mode');
    } else {
      console.log('Testing mode is disabled or no test email set');
    }
  } catch (error) {
    console.error('Error in setupTestEmailInterception:', error);
  }
}

// Initialize test email interception with a delayed call to avoid blocking app startup
setTimeout(() => {
  setupTestEmailInterception().catch(err => {
    console.error('Failed to initialize test email interception:', err);
  });
}, 1000);
